import { LIFECYCLES as REGISTRY_LIFECYCLES, isPublished } from '../integration/registry.js';

/**
 * The lifecycle catalogue — a pure PROJECTION of the central registry (`../integration/registry.ts`).
 * The per-stage `status` is DERIVED: a stage is `live` iff its event is a published event, else
 * `planned` — so promoting an event (adding it to the registry's EVENTS) flips its lifecycle stage to
 * `live` automatically, with no second edit. Consumed by the Catalogue page via `GET /api/hub/lifecycles`.
 */
export interface LifecycleState {
  readonly event: string;
  readonly state: string;
  readonly kind: 'initial' | 'transition' | 'success' | 'failure';
  readonly status: 'live' | 'planned';
}
export interface LifecycleTransition {
  readonly from: string;
  readonly to: string;
}
export interface Lifecycle {
  readonly id: string;
  readonly title: string;
  readonly service: string;
  readonly aggregate: string;
  readonly description: string;
  readonly states: readonly LifecycleState[];
  readonly transitions: readonly LifecycleTransition[];
}

/** Every catalogued lifecycle, with each stage's `status` derived from published-event membership. */
export const LIFECYCLE_CATALOGUE: readonly Lifecycle[] = Object.freeze(
  REGISTRY_LIFECYCLES.map((lc): Lifecycle => ({
    id: lc.id,
    title: lc.title,
    service: lc.service,
    aggregate: lc.aggregate,
    description: lc.description,
    states: lc.states.map((s) => ({ event: s.event, state: s.state, kind: s.kind, status: isPublished(s.event) ? 'live' : 'planned' })),
    transitions: lc.transitions.map((t) => ({ from: t.from, to: t.to })),
  })),
);

/** ISO date the lifecycle projection was last revised. */
export const LIFECYCLE_CATALOGUE_UPDATED = '2026-07-29';

/** Look up a lifecycle by id (undefined if not catalogued). */
export function lifecycleById(id: string): Lifecycle | undefined {
  return LIFECYCLE_CATALOGUE.find((lc) => lc.id === id);
}
